import { ReactNode, useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface ScheduleCreationSurfaceProps {
  page: boolean;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

export const ScheduleCreationSurface = ({
  page,
  open,
  onClose,
  children,
}: ScheduleCreationSurfaceProps) => {
  if (page) {
    return open ? (
      <Box
        component='main'
        sx={{
          width: '100%',
          maxWidth: 720,
          boxSizing: 'border-box',
          mx: 'auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight:
            'calc(100dvh - 56px - var(--app-safe-area-top) - var(--app-safe-area-bottom))',
          backgroundColor: 'background.paper',
        }}
      >
        {children}
      </Box>
    ) : null;
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      {children}
    </Dialog>
  );
};

export const ScheduleWizardHeader = ({
  title,
  step,
  onClose,
}: {
  title: string;
  step: number;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [step]);
  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='overline' color='primary' sx={{ fontWeight: 600 }}>
          {t('schedule.wizard.title')}
        </Typography>
        <IconButton
          aria-label={t('shared.cancel') || undefined}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography
        ref={titleRef}
        tabIndex={-1}
        component='h1'
        variant='h5'
        sx={{ mb: 1, outline: 'none' }}
      >
        {title}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {t('schedule.wizard.description')}
      </Typography>
      <Box
        role='status'
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
        }}
      >
        {t('schedule.wizard.step_count', {
          step: step + 1,
          label: t(
            `schedule.wizard.${
              ['details', 'special_dates', 'personnel'][step]
            }`,
          ),
        })}
      </Box>
      <Stepper
        activeStep={step}
        alternativeLabel
        sx={{ mt: 3, '& .MuiStepLabel-label': { fontSize: 11 } }}
      >
        {['details', 'special_dates', 'personnel'].map((key) => (
          <Step key={key}>
            <StepLabel>{t(`schedule.wizard.${key}`)}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};
